import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Trash2, CheckCircle, ListTodo, Clock, AlertTriangle, Paperclip, MessageSquare, RefreshCw, Pencil, Send } from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-700 border-slate-200',
};

const STATUS_COLORS = {
  pending: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-zinc-200 text-zinc-600',
};

const STATUS_LABEL = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const todayPlusHours = (h) => {
  const d = new Date();
  d.setHours(d.getHours() + h, 0, 0, 0);
  // Format for <input type="datetime-local"> (local time, no timezone)
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const initialForm = () => ({
  title: '',
  description: '',
  priority: 'medium',
  deadline: todayPlusHours(24),
  assigned_to_email: '',
});

export default function Tasks() {
  const { API, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: 'open', priority: 'all', assignee: 'all', q: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(initialForm());
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState({ open: false, task: null });
  const [commentBody, setCommentBody] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const myEmail = user?.email;
  const canManage = (t) => t?.assigned_by_email === myEmail || (user?.roles || []).includes('admin');

  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks`);
      setTasks(res.data || []);
    } catch (e) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [API]);

  const fetchAssignees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks/assignees`);
      setAssignees(res.data || []);
    } catch (e) {
      // non-fatal
    }
  }, [API]);

  useEffect(() => {
    fetchTasks();
    fetchAssignees();
    const t = setInterval(fetchTasks, 30000);
    return () => clearInterval(t);
  }, [fetchTasks, fetchAssignees]);

  const filtered = useMemo(() => {
    const now = new Date();
    return tasks.filter((t) => {
      if (filter.status === 'open' && !['pending', 'in_progress'].includes(t.status)) return false;
      if (filter.status === 'overdue') {
        if (!['pending', 'in_progress'].includes(t.status)) return false;
        if (new Date(t.deadline) >= now) return false;
      }
      if (!['open', 'overdue', 'all'].includes(filter.status) && t.status !== filter.status) return false;
      if (filter.priority !== 'all' && t.priority !== filter.priority) return false;
      if (filter.assignee === 'me' && t.assigned_to_email !== myEmail) return false;
      if (filter.assignee === 'by_me' && t.assigned_by_email !== myEmail) return false;
      if (filter.q) {
        const q = filter.q.toLowerCase();
        const hay = `${t.title} ${t.description || ''} ${t.assigned_to_name || ''} ${t.assigned_by_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, filter, myEmail]);

  const stats = useMemo(() => {
    const now = new Date();
    const open = tasks.filter((t) => ['pending', 'in_progress'].includes(t.status));
    return {
      total: tasks.length,
      open: open.length,
      overdue: open.filter((t) => new Date(t.deadline) < now).length,
      mine: open.filter((t) => t.assigned_to_email === myEmail).length,
      byMe: tasks.filter((t) => t.assigned_by_email === myEmail).length,
    };
  }, [tasks, myEmail]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Title is required');
    if (!form.assigned_to_email) return toast.error('Pick an assignee');
    if (!form.deadline) return toast.error('Pick a deadline');
    setSubmitting(true);
    try {
      // Convert local datetime to ISO UTC
      const deadlineIso = new Date(form.deadline).toISOString();
      await axios.post(`${API}/tasks`, {
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        deadline: deadlineIso,
        assigned_to_email: form.assigned_to_email,
      });
      toast.success('Task assigned. WhatsApp ping queued.');
      setCreateOpen(false);
      setForm(initialForm());
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (task, newStatus) => {
    try {
      await axios.post(`${API}/tasks/${task.task_id}/status`, { status: newStatus });
      toast.success(`Status updated → ${STATUS_LABEL[newStatus]}`);
      fetchTasks();
      if (detail.open && detail.task?.task_id === task.task_id) {
        const refreshed = await axios.get(`${API}/tasks/${task.task_id}`);
        setDetail({ open: true, task: refreshed.data });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const submitComment = async () => {
    if (!commentBody.trim() || !detail.task) return;
    try {
      await axios.post(`${API}/tasks/${detail.task.task_id}/comments`, { body: commentBody.trim() });
      setCommentBody('');
      const refreshed = await axios.get(`${API}/tasks/${detail.task.task_id}`);
      setDetail({ open: true, task: refreshed.data });
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add comment');
    }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/tasks/${task.task_id}`);
      toast.success('Task deleted');
      setDetail({ open: false, task: null });
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete task');
    }
  };

  const beginEdit = (task) => {
    setEditing(true);
    setEditForm({
      title: task.title,
      description: task.description || '',
      priority: task.priority,
      deadline: (() => {
        const d = new Date(task.deadline);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      })(),
      assigned_to_email: task.assigned_to_email,
    });
  };

  const submitEdit = async () => {
    if (!detail.task) return;
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        priority: editForm.priority,
        deadline: new Date(editForm.deadline).toISOString(),
        assigned_to_email: editForm.assigned_to_email,
      };
      await axios.put(`${API}/tasks/${detail.task.task_id}`, payload);
      toast.success('Task updated');
      const refreshed = await axios.get(`${API}/tasks/${detail.task.task_id}`);
      setDetail({ open: true, task: refreshed.data });
      setEditing(false);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update task');
    }
  };

  const isOverdue = (task) => ['pending', 'in_progress'].includes(task.status) && new Date(task.deadline) < new Date();

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-600">Loading tasks…</div>;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl lg:text-3xl font-semibold text-slate-900 tracking-tight">Task Manager</h2>
          <p className="text-sm text-slate-600 mt-1">Add tasks for yourself or a colleague, track deadlines, and get WhatsApp + on-screen reminders every 2 hours until completed.</p>
        </div>
        <Button onClick={() => { setForm((f) => ({ ...f, assigned_to_email: f.assigned_to_email || myEmail })); setCreateOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="create-task-btn">
          <Plus className="h-4 w-4 mr-1" /> New Task
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="cursor-pointer hover:shadow-md transition" onClick={() => setFilter((f) => ({ ...f, status: 'all', assignee: 'all' }))}>
          <CardContent className="py-3"><p className="text-xs text-slate-500">Total</p><p className="text-2xl font-bold">{stats.total}</p></CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition" onClick={() => setFilter((f) => ({ ...f, status: 'open', assignee: 'all' }))}>
          <CardContent className="py-3"><p className="text-xs text-slate-500">Open</p><p className="text-2xl font-bold text-blue-600">{stats.open}</p></CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition border-red-200" onClick={() => setFilter((f) => ({ ...f, status: 'overdue', assignee: 'all' }))}>
          <CardContent className="py-3"><p className="text-xs text-slate-500">Overdue</p><p className="text-2xl font-bold text-red-600">{stats.overdue}</p></CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition" onClick={() => setFilter((f) => ({ ...f, status: 'open', assignee: 'me' }))}>
          <CardContent className="py-3"><p className="text-xs text-slate-500">Assigned to me</p><p className="text-2xl font-bold text-indigo-600">{stats.mine}</p></CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition" onClick={() => setFilter((f) => ({ ...f, status: 'all', assignee: 'by_me' }))}>
          <CardContent className="py-3"><p className="text-xs text-slate-500">Created by me</p><p className="text-2xl font-bold text-violet-600">{stats.byMe}</p></CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="relative sm:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search title, description, people…" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} className="pl-10" data-testid="task-search" />
            </div>
            <Select value={filter.priority} onValueChange={(v) => setFilter({ ...filter, priority: v })}>
              <SelectTrigger data-testid="task-priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
              <SelectTrigger data-testid="task-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open (Pending + In Progress)</SelectItem>
                <SelectItem value="overdue">Overdue only</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="all">All statuses</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg"><ListTodo className="h-5 w-5" /> Tasks</CardTitle>
          <CardDescription>{filtered.length} task{filtered.length !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent className="p-0 lg:p-6 lg:pt-0">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500"><ListTodo className="mx-auto h-12 w-12 text-slate-300 mb-2" /><p>No tasks match your filters</p></div>
          ) : (
            <div className="hidden lg:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Assignee</TableHead>
                    <TableHead>Assigned by</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Deadline</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((t) => (
                    <TableRow key={t.task_id} data-testid={`task-row-${t.task_id}`} className={isOverdue(t) ? 'bg-red-50/40' : ''}>
                      <TableCell className="max-w-xs">
                        <button className="text-left font-medium text-slate-900 hover:text-indigo-600" onClick={() => setDetail({ open: true, task: t })} data-testid={`task-title-${t.task_id}`}>
                          {t.title}
                        </button>
                        {t.description && <p className="text-xs text-slate-500 line-clamp-1">{t.description}</p>}
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                          {t.attachments?.length > 0 && <span className="flex items-center gap-1"><Paperclip className="h-3 w-3" />{t.attachments.length}</span>}
                          {t.comments?.length > 0 && <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{t.comments.length}</span>}
                          {t.reminder_count > 0 && <span className="flex items-center gap-1"><RefreshCw className="h-3 w-3" />{t.reminder_count} reminder{t.reminder_count > 1 ? 's' : ''}</span>}
                        </div>
                      </TableCell>
                      <TableCell><span className="text-sm">{t.assigned_to_name || t.assigned_to_email}</span></TableCell>
                      <TableCell><span className="text-sm text-slate-600">{t.assigned_by_name || t.assigned_by_email}</span></TableCell>
                      <TableCell><Badge className={PRIORITY_COLORS[t.priority] + ' border'}>{t.priority}</Badge></TableCell>
                      <TableCell>
                        <div className="text-sm">{format(new Date(t.deadline), 'dd-MMM-yyyy HH:mm')}</div>
                        {isOverdue(t) ? (
                          <div className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {formatDistanceToNow(new Date(t.deadline))} overdue</div>
                        ) : ['pending', 'in_progress'].includes(t.status) ? (
                          <div className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" /> in {formatDistanceToNow(new Date(t.deadline))}</div>
                        ) : null}
                      </TableCell>
                      <TableCell><Badge className={STATUS_COLORS[t.status]}>{STATUS_LABEL[t.status]}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {t.status === 'pending' && (
                            <Button size="sm" variant="outline" className="text-blue-600" onClick={() => changeStatus(t, 'in_progress')} data-testid={`task-start-${t.task_id}`}>Start</Button>
                          )}
                          {['pending', 'in_progress'].includes(t.status) && (
                            <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => changeStatus(t, 'completed')} data-testid={`task-complete-${t.task_id}`}>
                              <CheckCircle className="h-4 w-4 mr-1" /> Done
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setDetail({ open: true, task: t })}>Open</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {/* Mobile */}
          <div className="lg:hidden space-y-3 p-4">
            {filtered.map((t) => (
              <div key={t.task_id} className={`border rounded-lg p-3 bg-white ${isOverdue(t) ? 'border-red-300' : 'border-slate-200'}`}>
                <div className="flex items-start justify-between">
                  <button className="text-left font-medium" onClick={() => setDetail({ open: true, task: t })}>{t.title}</button>
                  <Badge className={STATUS_COLORS[t.status]}>{STATUS_LABEL[t.status]}</Badge>
                </div>
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-600">
                  <Badge className={PRIORITY_COLORS[t.priority] + ' border'}>{t.priority}</Badge>
                  <span>→ {t.assigned_to_name || t.assigned_to_email}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{format(new Date(t.deadline), 'dd-MMM HH:mm')} {isOverdue(t) && <span className="text-red-600">· overdue</span>}</div>
                <div className="flex gap-2 mt-2">
                  {['pending', 'in_progress'].includes(t.status) && (
                    <Button size="sm" variant="outline" className="text-emerald-600 flex-1" onClick={() => changeStatus(t, 'completed')}>Mark Done</Button>
                  )}
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => setDetail({ open: true, task: t })}>Open</Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setForm(initialForm()); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Task</DialogTitle>
            <DialogDescription>The assignee will be pinged on WhatsApp immediately and every 2 hours until they mark the task Completed.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short, action-oriented" data-testid="task-title-input" autoFocus />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Optional context, links, expected outcome" data-testid="task-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="task-priority-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Deadline *</Label>
                <Input type="datetime-local" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="task-deadline-input" />
              </div>
            </div>
            <div>
              <Label>Assignee *</Label>
              <Select value={form.assigned_to_email} onValueChange={(v) => setForm({ ...form, assigned_to_email: v })}>
                <SelectTrigger data-testid="task-assignee-input"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {assignees.filter((a) => a.email === myEmail).map((a) => (
                    <SelectItem key="me" value={a.email} data-testid="task-assignee-myself">Myself — {a.full_name}</SelectItem>
                  ))}
                  {assignees.filter((a) => a.email !== myEmail).map((a) => (
                    <SelectItem key={a.email} value={a.email}>
                      {a.full_name} {a.has_phone ? '' : '· (no WhatsApp)'} <span className="text-xs text-slate-500">— {a.email}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.assigned_to_email && !(assignees.find((a) => a.email === form.assigned_to_email)?.has_phone) && (
                <p className="text-xs text-amber-600 mt-1">⚠ This user has no phone on file — WhatsApp reminders won't be sent. They'll still see the on-screen popup.</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="task-submit-btn">{submitting ? 'Creating…' : (<><Send className="h-4 w-4 mr-1" /> Assign Task</>)}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detail.open} onOpenChange={(o) => { if (!o) { setDetail({ open: false, task: null }); setEditing(false); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {detail.task && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <DialogTitle className="text-xl">{detail.task.title}</DialogTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={STATUS_COLORS[detail.task.status]}>{STATUS_LABEL[detail.task.status]}</Badge>
                      <Badge className={PRIORITY_COLORS[detail.task.priority] + ' border'}>{detail.task.priority}</Badge>
                      {isOverdue(detail.task) && <Badge className="bg-red-100 text-red-700">Overdue</Badge>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {!editing && canManage(detail.task) && !['completed', 'cancelled'].includes(detail.task.status) && (
                      <Button size="sm" variant="outline" onClick={() => beginEdit(detail.task)} title="Edit" data-testid="task-edit-btn"><Pencil className="h-4 w-4" /></Button>
                    )}
                    {canManage(detail.task) && (
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteTask(detail.task)} title="Delete" data-testid="task-delete-btn"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
              </DialogHeader>

              {editing ? (
                <div className="space-y-3 mt-2">
                  <div><Label>Title</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></div>
                  <div><Label>Description</Label><Textarea rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Priority</Label>
                      <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="urgent">Urgent</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Deadline</Label><Input type="datetime-local" value={editForm.deadline} onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })} /></div>
                  </div>
                  <div>
                    <Label>Assignee</Label>
                    <Select value={editForm.assigned_to_email} onValueChange={(v) => setEditForm({ ...editForm, assigned_to_email: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {assignees.map((a) => <SelectItem key={a.email} value={a.email}>{a.full_name} — {a.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                    <Button onClick={submitEdit}>Save</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                    <div><p className="text-slate-500">Assigned to</p><p className="font-medium">{detail.task.assigned_to_name || detail.task.assigned_to_email}</p>{detail.task.assigned_to_phone && <p className="text-xs text-slate-500">📱 {detail.task.assigned_to_phone}</p>}</div>
                    <div><p className="text-slate-500">Assigned by</p><p className="font-medium">{detail.task.assigned_by_name || detail.task.assigned_by_email}</p></div>
                    <div><p className="text-slate-500">Deadline</p><p className="font-medium">{format(new Date(detail.task.deadline), 'dd-MMM-yyyy HH:mm')}</p></div>
                    <div><p className="text-slate-500">Reminders sent</p><p className="font-medium">{detail.task.reminder_count || 0}{detail.task.last_reminder_sent_at && <span className="text-xs text-slate-500"> · last {formatDistanceToNow(new Date(detail.task.last_reminder_sent_at))} ago</span>}</p></div>
                  </div>

                  {detail.task.description && (
                    <div className="mt-3"><p className="text-xs text-slate-500 mb-1">Description</p><p className="text-sm whitespace-pre-wrap bg-slate-50 p-3 rounded">{detail.task.description}</p></div>
                  )}

                  {/* Status actions */}
                  <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t">
                    {detail.task.status === 'pending' && (
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => changeStatus(detail.task, 'in_progress')}>Start</Button>
                    )}
                    {['pending', 'in_progress'].includes(detail.task.status) && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => changeStatus(detail.task, 'completed')} data-testid="task-mark-complete">
                        <CheckCircle className="h-4 w-4 mr-1" /> Mark Completed
                      </Button>
                    )}
                    {canManage(detail.task) && ['pending', 'in_progress'].includes(detail.task.status) && (
                      <Button size="sm" variant="outline" onClick={() => changeStatus(detail.task, 'cancelled')} data-testid="task-cancel-btn">Cancel Task</Button>
                    )}
                    {['completed', 'cancelled'].includes(detail.task.status) && (
                      <Button size="sm" variant="outline" onClick={() => changeStatus(detail.task, 'pending')}>Re-open</Button>
                    )}
                  </div>

                  {/* Comments */}
                  <div className="mt-5">
                    <p className="text-sm font-semibold mb-2 flex items-center gap-1"><MessageSquare className="h-4 w-4" /> Comments ({detail.task.comments?.length || 0})</p>
                    <div className="space-y-2 mb-2 max-h-56 overflow-y-auto">
                      {(detail.task.comments || []).map((c) => (
                        <div key={c.comment_id} className="bg-slate-50 rounded p-2 text-sm">
                          <p className="text-xs text-slate-500">{c.author_name || c.author_email} · {format(new Date(c.created_at), 'dd-MMM HH:mm')}</p>
                          <p className="whitespace-pre-wrap">{c.body}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input placeholder="Add a comment…" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submitComment(); }} data-testid="task-comment-input" />
                      <Button onClick={submitComment} disabled={!commentBody.trim()} data-testid="task-comment-submit">Post</Button>
                    </div>
                  </div>

                  {/* Activity log */}
                  <details className="mt-4">
                    <summary className="text-sm text-slate-600 cursor-pointer hover:text-slate-900">Activity log ({detail.task.activity?.length || 0})</summary>
                    <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                      {(detail.task.activity || []).slice().reverse().map((a, i) => (
                        <div key={i} className="text-xs text-slate-600 border-l-2 border-slate-200 pl-2 py-0.5">
                          <span className="text-slate-400">{format(new Date(a.at), 'dd-MMM HH:mm')}</span> · {a.message}
                          {a.by_name && <span className="text-slate-400"> — {a.by_name}</span>}
                        </div>
                      ))}
                    </div>
                  </details>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
